import type { Map as MapboxMap } from 'mapbox-gl';
import capturedAadtMarker1 from '@/assets/icons/captured-aadt-marker-1.svg?raw';
import capturedAadtMarker2 from '@/assets/icons/captured-aadt-marker-2.svg?raw';
import locationMarker from '@/assets/icons/location-marker.svg?raw';
import locationMarkerSelected from '@/assets/icons/location-marker-selected.svg?raw';
import {
  CAPTURED_AADT_MARKER_ICONS,
  CAPTURED_AADT_SEGMENT_COLORS,
  MarkKind,
  STATION_COLORS,
} from '../constants';
import {
  electraMarkerId,
  electraMarkerSvg,
  STATION_ICONS,
  STATION_MARKER_HEIGHT,
  STATION_MARKER_WIDTH,
} from '../constants/stationIcons';
import { addSvgIconViaCanvas } from './addSvgIconViaCanvas';
import {
  HIGHEST_COMPETITOR_CROWN_HEIGHT,
  HIGHEST_COMPETITOR_CROWN_ICON,
  HIGHEST_COMPETITOR_CROWN_WIDTH,
  highestCompetitorCrownSvg,
} from './highestCompetitorCrown';
import { ZONE_TYPE_COLORS, ZONE_TYPES } from './zoneColors';
import { SELECTED_ZONE_HEX_ICON, SELECTED_ZONE_HEX_SIZE, zoneHexagonSvg } from './zoneHexagon';
import {
  buildClusterChipSvg,
  buildClusterGlyphSvg,
  CHIP_SIZE_STEPS,
  clusterChipGeometry,
  clusterChipIconId,
  CLUSTER_GLYPH_CANVAS,
  CLUSTER_GLYPH_ID,
} from './clusterChipMark';

/**
 * Initializes all map icons/markers by loading SVG assets into the Mapbox map.
 * This includes location markers, competitor station markers of various sizes, and Electra markers.
 */
export async function initializeMapIcons(map: MapboxMap): Promise<void> {
  try {
    // Location markers
    if (!map.hasImage('marker-location')) {
      await addSvgIconViaCanvas(map, 'marker-location', locationMarker, {
        height: 44,
      });
    }
    if (!map.hasImage('marker-location-selected')) {
      await addSvgIconViaCanvas(map, 'marker-location-selected', locationMarkerSelected, {
        height: 44,
      });
    }

    // Here rather than with the deal marks: the chip marks every cluster, so a location cluster
    // must not depend on the deals flag for something to draw.
    for (const step of CHIP_SIZE_STEPS) {
      const chipId = clusterChipIconId(step.px);
      if (map.hasImage(chipId)) continue;
      const geometry = clusterChipGeometry(step.px);
      await addSvgIconViaCanvas(map, chipId, buildClusterChipSvg(step.px), {
        width: geometry.width,
        height: geometry.height,
        stretchX: geometry.stretchX,
        content: geometry.content,
      });
    }
    for (const kind of Object.values(MarkKind)) {
      const glyphId = CLUSTER_GLYPH_ID[kind];
      if (map.hasImage(glyphId)) continue;
      await addSvgIconViaCanvas(map, glyphId, buildClusterGlyphSvg(kind), {
        width: CLUSTER_GLYPH_CANVAS,
        height: CLUSTER_GLYPH_CANVAS,
      });
    }

    for (const type of ZONE_TYPES) {
      const id = `${SELECTED_ZONE_HEX_ICON}-${type}`;
      if (map.hasImage(id)) continue;
      await addSvgIconViaCanvas(map, id, zoneHexagonSvg(ZONE_TYPE_COLORS[type]), {
        width: SELECTED_ZONE_HEX_SIZE,
        height: SELECTED_ZONE_HEX_SIZE,
      });
    }

    if (!map.hasImage(HIGHEST_COMPETITOR_CROWN_ICON)) {
      await addSvgIconViaCanvas(map, HIGHEST_COMPETITOR_CROWN_ICON, highestCompetitorCrownSvg(), {
        height: HIGHEST_COMPETITOR_CROWN_HEIGHT,
        width: HIGHEST_COMPETITOR_CROWN_WIDTH,
      });
    }

    // Competitor station markers - using centralized STATION_ICONS
    for (const stationColor of Object.values(STATION_COLORS)) {
      const { id, rangeKey } = stationColor;
      if (!map.hasImage(id)) {
        const markerSvg = STATION_ICONS[rangeKey].marker;
        await addSvgIconViaCanvas(map, id, markerSvg, {
          height: STATION_MARKER_HEIGHT,
          width: STATION_MARKER_WIDTH,
        });
      }
      const electraId = electraMarkerId(rangeKey);
      if (!map.hasImage(electraId)) {
        await addSvgIconViaCanvas(map, electraId, electraMarkerSvg(stationColor.color), {
          height: STATION_MARKER_HEIGHT,
          width: STATION_MARKER_WIDTH,
        });
      }
    }

    // Captured AADT segment markers
    const segment0MarkerIcon =
      CAPTURED_AADT_MARKER_ICONS[CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.id];
    if (!map.hasImage(segment0MarkerIcon)) {
      await addSvgIconViaCanvas(map, segment0MarkerIcon, capturedAadtMarker1, {
        height: 28,
        width: 28,
      });
    }
    const segment1MarkerIcon =
      CAPTURED_AADT_MARKER_ICONS[CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_1.id];
    if (!map.hasImage(segment1MarkerIcon)) {
      await addSvgIconViaCanvas(map, segment1MarkerIcon, capturedAadtMarker2, {
        height: 28,
        width: 28,
      });
    }
  } catch {
    throw new Error('Error adding image assets to map');
  }
}
